/******************************************************************************
 *
 * FILE:
 * src/modules/wms/workspace/execution/pages/ExecutionWorkspacePage.jsx
 *
 * MODULE:
 * Work Management System (WMS)
 *
 * FEATURE:
 * Execution Workspace
 *
 * DESCRIPTION:
 * Main execution workspace page.
 *
 * RESPONSIBILITIES
 * ----------------
 * ✓ Load execution workspace
 * ✓ Maintain page state
 * ✓ Delegate actions to child components
 * ✓ No business logic
 *
 ******************************************************************************/

import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import { useParams } from "react-router-dom";

import executionWorkspaceService
from "../services/executionWorkspaceService";

import ExecutionHeader
from "../components/ExecutionHeader";

import ExecutionInformationPanel
from "../components/ExecutionInformationPanel";

import ChecklistExecutionPanel
from "../components/ChecklistExecutionPanel";

import ObservationPanel
from "../components/ObservationPanel";

import AttachmentPanel
from "../components/AttachmentPanel";

import CommentPanel
from "../components/CommentPanel";

import ActivityTimeline
from "../components/ActivityTimeline";

import ExecutionFooter
from "../components/ExecutionFooter";

export default function ExecutionWorkspacePage() {

    // ==========================================================
    // ROUTE
    // ==========================================================

    const { workId } = useParams();

    // ==========================================================
    // STATE
    // ==========================================================

    const [loading, setLoading] =
        useState(true);

    const [workspace, setWorkspace] =
        useState(null);

    const [saving, setSaving] =
        useState(false);

    const [completing, setCompleting] =
        useState(false);

    const [lastSaved, setLastSaved] =
    useState(null);    
    
    async function handleResponseChange(item, value) {

    try {

        // -----------------------------------------------------
        // Save to Backend
        // -----------------------------------------------------

        const result =
            await executionWorkspaceService
                .updateChecklistItem(

                    workspace.work.id,

                    item.id,

                    {

                        response: value,

                        remarks: item.remarks || "",

                    }

                );

        // -----------------------------------------------------
        // Update UI
        // -----------------------------------------------------

        setWorkspace((prev) => {

            const checklist = prev.checklist.map((section) => ({

                ...section,

                items: section.items.map((row) =>

                    row.id === item.id

                        ? {

                            ...row,

                            response: value,

                            completed: true,

                        }

                        : row

                ),

            }));

            return {

                ...prev,

                checklist,

                summary: result.summary,

                work: {

                    ...prev.work,

                    progress:
                        result.summary.progress_percentage,

                },

            };

        });

    }

    catch (error) {

        console.error(
            "Unable to save response",
            error
        );

        alert(
            "Unable to save checklist response."
        );

    }

}
 async function handleRemarksChange(item, remarks) {

    try {

        // -----------------------------------------------------
        // Save Remarks to Backend
        // -----------------------------------------------------

        await executionWorkspaceService.updateChecklistItem(

            workspace.work.id,

            item.id,

            {

                response: item.response,

                remarks,

            }

        );

        // -----------------------------------------------------
        // Update UI
        // -----------------------------------------------------

        setWorkspace((prev) => ({

            ...prev,

            checklist: prev.checklist.map((section) => ({

                ...section,

                items: section.items.map((row) =>

                    row.id === item.id

                        ? {

                            ...row,

                            remarks,

                        }

                        : row

                ),

            })),

        }));

    }

    catch (error) {

        console.error(

            "Unable to save remarks",

            error

        );

        alert(

            "Unable to save remarks."

        );

    }

}   
    
    // ==========================================================
    // LOAD
    // ==========================================================

    const loadWorkspace =
        useCallback(async () => {

            try {

                setLoading(true);

                const response =
                    await executionWorkspaceService
                        .getExecutionWorkspace(workId);

                setWorkspace(response);

            }

            finally {

                setLoading(false);

            }

        }, [workId]);

    useEffect(() => {

        loadWorkspace();

    }, [loadWorkspace]);

    // ==========================================================
    // ACTIONS
    // ==========================================================

    async function handleSave() {

        console.log("========== SAVE CLICKED ==========");
        console.log(workspace);
        if (saving) {

            return;

        }

        try {

            setSaving(true);

            await executionWorkspaceService
                .saveProgress(workspace);
            setLastSaved(
                new Date().toLocaleString()
            );

        }

        finally {

            setSaving(false);

        }

    }

    async function handleComplete() {

    if (completing) {

        return;

    }

    try {

        setCompleting(true);

        const result =
            await executionWorkspaceService.completeWork(

                workspace.work.id,

                ""

            );

        alert(result.message);

        await loadWorkspace();

    }

    catch (error) {

        console.error(
            "Unable to complete work",
            error
        );

        alert(
            error?.response?.data?.message ||
            "Unable to complete work."
        );

    }

    finally {

        setCompleting(false);

    }

}

async function handleAddComment(commentText) {

    try {

        await executionWorkspaceService.addComment(

            workspace.work.id,

            commentText

        );

        await loadWorkspace();

    }

    catch (error) {

        console.error(

            "Unable to add comment",

            error

        );

        alert(

            error?.response?.data?.message ||

            "Unable to add comment."

        );

    }

}
    // ==========================================================
    // LOADING
    // ==========================================================

    if (loading) {

        return (

            <div className="container-fluid py-5">

                <div className="text-center">

                    <div className="spinner-border text-primary" />

                    <div className="mt-3">

                        Loading Execution Workspace...

                    </div>

                </div>

            </div>

        );

    }

    if (!workspace) {

        return null;

    }

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

    <>
        {(saving || completing) && (
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(255,255,255,0.75)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 9999,
                }}
            >
                <div className="text-center">
                    <div
                        className="spinner-border text-primary"
                        style={{ width: "3rem", height: "3rem" }}
                    />
                    <div className="mt-3 fw-semibold">
                        {completing
                            ? "Completing Work..."
                            : "Saving Progress..."}
                    </div>
                </div>
            </div>
        )}

        <div className="container-fluid pb-5">
        
            

            <ExecutionHeader

                work={workspace.work}

            />

            <ExecutionInformationPanel

                work={workspace.work}

                summary={workspace.summary}

            />

            <ChecklistExecutionPanel

                checklist={workspace.checklist}

                onResponseChange={handleResponseChange}

                onRemarksChange={handleRemarksChange}

            />

            <ObservationPanel

                observations={workspace.observations}

            />

            <AttachmentPanel

                attachments={workspace.attachments}

            />

            <CommentPanel

                comments={workspace.comments}

                onAddComment={handleAddComment}

            />

            <ActivityTimeline

                timeline={workspace.timeline}

            />

            <ExecutionFooter

                lastSaved={
                    lastSaved ||
                    workspace.work.updated_at
                }

                saving={saving}

                completing={completing}

                onSave={handleSave}

                onComplete={handleComplete}

            />

        </div>
         </>         
    );

}